import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Globe,
  Shield,
  Zap,
  Download,
  AlertCircle,
  CheckCircle,
  User,
  Mail,
  Phone,
  Building,
  MessageSquare,
  X,
} from "lucide-react";
import axios from "axios";
import { db } from "../../firebase/config";
import { collection, addDoc } from "firebase/firestore";

interface AuditResult {
  url: string;
  auditData: {
    performance: number;
    accessibility: number;
    bestPractices: number;
    seo: number;
    metrics: {
      lcp: string;
      cls: string;
      inp: string;
      fcp: string;
    };
    title: string;
    description: string;
  };
  aiAnalysis: {
    readinessScore: number;
    status: string;
    summary: string;
    keyFindings: string[];
    recommendations: Array<{ area: string; priority: string; action: string }>;
    futureProofing: string;
  };
}

interface LeadFormData {
  fullName: string;
  email: string;
  whatsapp: string;
  company: string;
  message: string;
  consent: boolean;
}

const AuditScanner: React.FC = () => {
  const [url, setUrl] = useState("");
  const [isScanning, setIsScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<AuditResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  const [leadForm, setLeadForm] = useState<LeadFormData>({
    fullName: "",
    email: "",
    whatsapp: "",
    company: "",
    message: "",
    consent: false,
  });

  const handleScan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url) return;

    setIsScanning(true);
    setError(null);
    setResult(null);
    setProgress(10);

    let progressInterval: any;

    try {
      progressInterval = setInterval(() => {
        setProgress((prev) => (prev < 90 ? prev + 2 : prev));
      }, 500);

      const response = await axios.post("/.netlify/functions/audit", { url });

      clearInterval(progressInterval);
      setProgress(100);

      setTimeout(() => {
        setResult(response.data);
        setIsScanning(false);
      }, 500);
    } catch (err: any) {
      if (progressInterval) clearInterval(progressInterval);
      console.error("Audit Scanner Error:", err);

      let message =
        "Failed to analyze website. Please check the URL and try again.";

      if (err.response) {
        message = `Server Error: ${err.response.data?.error || err.response.data?.details || err.response.statusText} (${err.response.status})`;
        if (err.response.status === 404) {
          message =
            'Audit service not found. LOCAL TEST: Use "netlify dev" instead of "npm start" to enable functions.';
        }
      } else if (err.request) {
        message =
          "Connectivity Error: No response from server. Check if the backend is running.";
      } else {
        message = `Request Error: ${err.message}`;
      }

      setError(message);
      setIsScanning(false);
    }
  };

  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadForm.consent) {
      alert("Please provide consent to proceed.");
      return;
    }

    setIsSubmittingLead(true);
    try {
      console.log("[Lead] Submitting form data...");

      // Flatten the data to ensure perfect serialization
      const submissionData = {
        fullName: String(leadForm.fullName),
        email: String(leadForm.email),
        whatsapp: String(leadForm.whatsapp),
        company: String(leadForm.company),
        message: String(leadForm.message),
        consent: Boolean(leadForm.consent),
        scannedUrl: String(result?.url || "N/A"),
        scannedAt: new Date().toISOString(),
        readinessScore: Number(result?.aiAnalysis.readinessScore || 0),
        performanceScore: Number(result?.auditData.performance || 0),
        source: "2026_readiness_scanner",
      };

      console.log("[Lead] Submission Payload:", submissionData);

      const docRef = await addDoc(
        collection(db, "mw-portfolio-audit-site-request"),
        submissionData,
      );
      console.log("[Lead] Submission Success! DocID:", docRef.id);

      setShowLeadForm(false);
      executePDFDownload();
    } catch (err: any) {
      console.error("[Lead] Detailed Submission Error:", err);
      alert(
        `Submission Error: ${err.code || "Firebase Error"}\nMessage: ${err.message}`,
      );
    } finally {
      setIsSubmittingLead(false);
    }
  };

  const executePDFDownload = async () => {
    if (!result) return;

    try {
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF();
      const { auditData, aiAnalysis, url: scannedUrl } = result;

      doc.setFillColor(31, 41, 55);
      doc.rect(0, 0, 210, 40, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.text("2026 Website Readiness Report", 20, 25);

      doc.setTextColor(0, 0, 0);
      doc.setFontSize(14);
      doc.text(`URL: ${scannedUrl}`, 20, 50);
      doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 60);

      doc.setFontSize(18);
      doc.text(`Readiness Score: ${aiAnalysis.readinessScore}%`, 20, 75);
      doc.text(`Status: ${aiAnalysis.status}`, 20, 85);

      doc.setFontSize(12);
      const summaryLines = doc.splitTextToSize(aiAnalysis.summary, 170);
      doc.text(summaryLines, 20, 95);

      doc.setFontSize(16);
      doc.text("Core Technical Metrics", 20, 130);
      doc.setFontSize(10);
      doc.text(`Performance: ${auditData.performance}%`, 25, 140);
      doc.text(`Accessibility: ${auditData.accessibility}%`, 25, 145);
      doc.text(`SEO: ${auditData.seo}%`, 25, 150);
      doc.text(`LCP: ${auditData.metrics.lcp}`, 25, 155);

      doc.setFontSize(16);
      doc.text("Key Recommendations", 20, 170);
      let y = 180;
      aiAnalysis.recommendations.slice(0, 5).forEach((rec) => {
        doc.setFontSize(11);
        doc.text(`• [${rec.priority}] ${rec.area}: ${rec.action}`, 25, y);
        y += 10;
      });

      doc.setFontSize(10);
      doc.setTextColor(107, 114, 128);
      doc.text("Generated by MoizWP AI Audit Tool", 20, 280);

      doc.save(`Audit_Report_${scannedUrl.replace(/[^a-z0-9]/gi, "_")}.pdf`);
    } catch (error) {
      console.error("Failed to load jspdf:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  const triggerDownload = () => {
    setShowLeadForm(true);
  };

  return (
    <section className="py-20 bg-gray-50 overflow-hidden" id="audit-tool">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 font-display">
            2026 Readiness Scanner
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Enter your website URL to get an AI-powered technical audit against
            the standards of 2026.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <form
            onSubmit={handleScan}
            className="relative flex flex-col sm:flex-row gap-4"
          >
            <div className="relative flex-grow">
              <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="url"
                placeholder="https://yourwebsite.com"
                required
                className="w-full pl-12 pr-4 py-4 rounded-2xl border-2 border-gray-200 focus:border-blue-500 outline-none transition-all text-gray-700 md:text-lg bg-white shadow-sm"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
            </div>
            <button
              type="submit"
              disabled={isScanning}
              className={`bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-2xl font-bold md:text-lg transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-blue-500/20 ${
                isScanning
                  ? "opacity-70 cursor-not-allowed"
                  : "hover:scale-105 active:scale-95"
              }`}
            >
              {isScanning ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Scanning...
                </>
              ) : (
                <>
                  <Zap className="w-5 h-5" />
                  Analyze Now
                </>
              )}
            </button>
          </form>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mt-6 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3 text-red-700 shadow-sm"
              >
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm md:text-base font-medium">{error}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Scanning Overlay */}
        <AnimatePresence>
          {isScanning && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] bg-gray-900/90 backdrop-blur-md flex items-center justify-center p-4 text-center"
            >
              <div className="max-w-md w-full">
                <div className="mb-10 relative mx-auto w-40 h-40">
                  <motion.div
                    animate={{ rotate: 360, scale: [1, 1.1, 1] }}
                    transition={{ duration: 3, repeat: Infinity }}
                    className="absolute inset-0 border-4 border-blue-500/10 rounded-full"
                  />
                  <motion.div
                    animate={{ rotate: -360 }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                    className="absolute inset-4 border-4 border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent rounded-full"
                  />
                  <Shield className="absolute inset-0 m-auto w-16 h-16 text-blue-500" />
                </div>

                <h3 className="text-3xl font-bold text-white mb-3">
                  AI Technical Deep-Dive
                </h3>
                <p className="text-blue-200 mb-10 text-lg">
                  We are auditing performance, security, and edge-readiness...
                </p>

                <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden shadow-inner">
                  <motion.div
                    className="bg-gradient-to-r from-blue-400 to-blue-600 h-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-white/60 mt-3 text-sm font-medium uppercase tracking-widest">
                  {Math.round(progress)}% Processed
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Results View */}
        <AnimatePresence>
          {result && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mt-16 bg-white rounded-[2rem] shadow-2xl overflow-hidden max-w-6xl mx-auto border border-gray-100"
            >
              <div className="bg-gray-900 p-8 md:p-12 text-white flex flex-col lg:flex-row justify-between items-center gap-8">
                <div className="flex items-center gap-8">
                  <div className="relative">
                    <svg className="w-24 h-24 transform -rotate-90">
                      <circle
                        cx="48"
                        cy="48"
                        r="44"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="transparent"
                        className="text-gray-800"
                      />
                      <circle
                        cx="48"
                        cy="48"
                        r="44"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="transparent"
                        className="text-blue-500"
                        strokeDasharray={276}
                        strokeDashoffset={
                          276 - (276 * result.aiAnalysis.readinessScore) / 100
                        }
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center font-bold text-2xl">
                      {result.aiAnalysis.readinessScore}%
                    </div>
                  </div>
                  <div>
                    <h3 className="text-2xl md:text-3xl font-bold mb-2">
                      2026 Readiness Score
                    </h3>
                    <p className="text-blue-400 flex items-center gap-2 font-medium">
                      <CheckCircle className="w-5 h-5" />{" "}
                      {result.aiAnalysis.status} Status
                    </p>
                  </div>
                </div>
                <button
                  onClick={triggerDownload}
                  className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-bold flex items-center gap-3 hover:bg-blue-700 transition-all shadow-xl hover:shadow-blue-500/40"
                >
                  <Download className="w-6 h-6" />
                  Download Full Report
                </button>
              </div>

              <div className="p-8 md:p-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {[
                  {
                    label: "Performance",
                    val: result.auditData.performance,
                    icon: Zap,
                    color: "text-yellow-500",
                    bg: "bg-yellow-50",
                  },
                  {
                    label: "Accessibility",
                    val: result.auditData.accessibility,
                    icon: Shield,
                    color: "text-green-500",
                    bg: "bg-green-50",
                  },
                  {
                    label: "SEO Score",
                    val: result.auditData.seo,
                    icon: Search,
                    color: "text-blue-500",
                    bg: "bg-blue-50",
                  },
                  {
                    label: "Security",
                    val: result.auditData.bestPractices,
                    icon: CheckCircle,
                    color: "text-purple-500",
                    bg: "bg-purple-50",
                  },
                ].map((stat, i) => (
                  <div
                    key={i}
                    className={`${stat.bg} p-8 rounded-3xl border border-transparent hover:border-gray-200 transition-all group`}
                  >
                    <stat.icon
                      className={`w-8 h-8 mb-6 ${stat.color} group-hover:scale-110 transition-transform`}
                    />
                    <div className="text-4xl font-bold mb-2 text-gray-900">
                      {stat.val}%
                    </div>
                    <div className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-8 md:p-12 border-t border-gray-100 grid grid-cols-1 lg:grid-cols-2 gap-16">
                <div>
                  <h4 className="text-2xl font-bold mb-8 flex items-center gap-3 text-gray-900 font-display">
                    <CheckCircle className="text-blue-500 w-8 h-8" /> Executive
                    Summary
                  </h4>
                  <div className="text-gray-600 leading-relaxed text-lg mb-10">
                    <p>{result.aiAnalysis.summary}</p>
                  </div>
                  <div className="bg-gray-50 p-8 rounded-3xl border border-gray-100 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100 rounded-full -mr-16 -mt-16 opacity-50" />
                    <h5 className="font-bold text-gray-900 mb-4 text-xl relative z-10">
                      Future Proofing Analysis
                    </h5>
                    <p className="text-gray-600 leading-relaxed relative z-10">
                      {result.aiAnalysis.futureProofing}
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="text-2xl font-bold mb-8 text-gray-900 font-display">
                    Strategic Recommendations
                  </h4>
                  <div className="space-y-6">
                    {result.aiAnalysis.recommendations.map((rec, i) => (
                      <div
                        key={i}
                        className="flex gap-6 p-6 rounded-3xl border border-gray-100 hover:bg-gray-50 transition-all"
                      >
                        <div
                          className={`w-3 h-3 rounded-full mt-3 shrink-0 shadow-sm ${
                            rec.priority === "High"
                              ? "bg-red-500 shadow-red-200"
                              : rec.priority === "Medium"
                                ? "bg-yellow-500 shadow-yellow-200"
                                : "bg-blue-500 shadow-blue-200"
                          }`}
                        />
                        <div>
                          <p className="font-bold text-gray-900 mb-2 md:text-lg">
                            {rec.area}
                          </p>
                          <p className="text-gray-600 leading-relaxed">
                            {rec.action}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Lead Generation Modal */}
      <AnimatePresence>
        {showLeadForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-gray-900/80 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-white rounded-[2rem] shadow-2xl max-w-lg w-full overflow-hidden relative"
            >
              <button
                onClick={() => setShowLeadForm(false)}
                className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="p-8 md:p-10">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  Download Detailed Report
                </h3>
                <p className="text-gray-500 mb-8">
                  Please provide your details to receive the full technical
                  analysis.
                </p>

                <form onSubmit={handleLeadSubmit} className="space-y-5">
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Full Name"
                      required
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 rounded-xl border border-gray-200 focus:border-blue-500 focus:bg-white outline-none transition-all"
                      value={leadForm.fullName}
                      onChange={(e) =>
                        setLeadForm({ ...leadForm, fullName: e.target.value })
                      }
                    />
                  </div>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="email"
                      placeholder="Work Email"
                      required
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 rounded-xl border border-gray-200 focus:border-blue-500 focus:bg-white outline-none transition-all"
                      value={leadForm.email}
                      onChange={(e) =>
                        setLeadForm({ ...leadForm, email: e.target.value })
                      }
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="tel"
                        placeholder="WhatsApp Number"
                        required
                        className="w-full pl-12 pr-4 py-4 bg-gray-50 rounded-xl border border-gray-200 focus:border-blue-500 focus:bg-white outline-none transition-all"
                        value={leadForm.whatsapp}
                        onChange={(e) =>
                          setLeadForm({ ...leadForm, whatsapp: e.target.value })
                        }
                      />
                    </div>
                    <div className="relative">
                      <Building className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        placeholder="Company Name"
                        required
                        className="w-full pl-12 pr-4 py-4 bg-gray-50 rounded-xl border border-gray-200 focus:border-blue-500 focus:bg-white outline-none transition-all"
                        value={leadForm.company}
                        onChange={(e) =>
                          setLeadForm({ ...leadForm, company: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="relative">
                    <MessageSquare className="absolute left-4 top-4 text-gray-400 w-5 h-5" />
                    <textarea
                      placeholder="Briefly describe your project goals..."
                      rows={3}
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 rounded-xl border border-gray-200 focus:border-blue-500 focus:bg-white outline-none transition-all resize-none"
                      value={leadForm.message}
                      onChange={(e) =>
                        setLeadForm({ ...leadForm, message: e.target.value })
                      }
                    />
                  </div>

                  <div className="flex items-start gap-3 py-2">
                    <input
                      type="checkbox"
                      id="consent"
                      className="mt-1 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      checked={leadForm.consent}
                      onChange={(e) =>
                        setLeadForm({ ...leadForm, consent: e.target.checked })
                      }
                    />
                    <label
                      htmlFor="consent"
                      className="text-sm text-gray-500 leading-snug cursor-pointer"
                    >
                      I consent to being contacted regarding this audit. My
                      information is protected and will not be shared.
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmittingLead}
                    className="w-full bg-blue-600 text-white py-5 rounded-2xl font-bold flex items-center justify-center gap-3 hover:bg-blue-700 transition-all shadow-xl hover:shadow-blue-500/30 disabled:opacity-70"
                  >
                    {isSubmittingLead ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>Submit & Download PDF</>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default AuditScanner;
